import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  RadioGroup,
} from '@/components/ui';
import {
  EXTERNAL_SCRIPT_CATEGORIES,
  EXTERNAL_SCRIPT_CATEGORY_LABELS,
  EXTERNAL_SCRIPT_STATUS_LABELS,
  EXTERNAL_SCRIPT_STATUSES,
  SCRIPT_PLACEMENT_LABELS,
  SCRIPT_PLACEMENTS,
  type ExternalScriptCategory,
  type ExternalScriptStatus,
  type ExternalServiceScript,
  type ExternalServiceScriptPayload,
  type ScriptPlacement,
  type StoreScope,
} from '../types/externalService';

interface ExternalServiceEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 편집 대상. null 이면 신규 생성. */
  target: ExternalServiceScript | null;
  /** 현재 탭의 상점 스코프(신규 생성 시 기본값). */
  storeScope: StoreScope;
  saving: boolean;
  onSubmit: (payload: ExternalServiceScriptPayload) => void;
}

interface EditorDraft {
  storeScope: StoreScope;
  category: ExternalScriptCategory;
  serviceName: string;
  measurementId: string;
  scriptContent: string;
  placement: ScriptPlacement;
  status: ExternalScriptStatus;
  sortOrder: string;
}

const emptyDraft = (storeScope: StoreScope): EditorDraft => ({
  storeScope,
  category: 'CUSTOM',
  serviceName: '',
  measurementId: '',
  scriptContent: '',
  placement: 'HEAD',
  status: 'OFF',
  sortOrder: '0',
});

const toDraft = (target: ExternalServiceScript): EditorDraft => ({
  storeScope: target.storeScope,
  category: target.category,
  serviceName: target.serviceName,
  measurementId: target.measurementId ?? '',
  scriptContent: target.scriptContent ?? '',
  placement: target.placement,
  status: target.status,
  sortOrder: String(target.sortOrder),
});

const categoryOptions = EXTERNAL_SCRIPT_CATEGORIES.map((c) => ({
  value: c,
  label: EXTERNAL_SCRIPT_CATEGORY_LABELS[c],
}));
const placementOptions = SCRIPT_PLACEMENTS.map((p) => ({ value: p, label: SCRIPT_PLACEMENT_LABELS[p] }));
const statusOptions = EXTERNAL_SCRIPT_STATUSES.map((s) => ({
  value: s,
  label: EXTERNAL_SCRIPT_STATUS_LABELS[s],
}));

/**
 * 외부 서비스 스크립트 생성/편집 다이얼로그.
 * GA 카테고리면 measurementId 만, 그 외에는 scriptContent 를 입력한다.
 */
export const ExternalServiceEditor = ({
  open,
  onOpenChange,
  target,
  storeScope,
  saving,
  onSubmit,
}: ExternalServiceEditorProps) => {
  const [draft, setDraft] = useState<EditorDraft>(() =>
    target ? toDraft(target) : emptyDraft(storeScope),
  );
  const [error, setError] = useState<string | null>(null);

  // 다이얼로그가 열릴 때마다 대상/스코프에 맞춰 draft 초기화.
  useEffect(() => {
    if (open) {
      setDraft(target ? toDraft(target) : emptyDraft(storeScope));
      setError(null);
    }
  }, [open, target, storeScope]);

  const isGa = draft.category === 'GA';

  const set = <K extends keyof EditorDraft>(key: K, val: EditorDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = () => {
    setError(null);
    if (draft.serviceName.trim() === '') {
      setError('서비스명을 입력하세요.');
      return;
    }
    if (isGa && draft.measurementId.trim() === '') {
      setError('GA 측정 ID를 입력하세요.');
      return;
    }
    if (!isGa && draft.scriptContent.trim() === '') {
      setError('스크립트 내용을 입력하세요.');
      return;
    }
    const sortOrder = Number.parseInt(draft.sortOrder, 10);
    onSubmit({
      storeScope: draft.storeScope,
      category: draft.category,
      serviceName: draft.serviceName.trim(),
      measurementId: isGa ? draft.measurementId.trim() : null,
      scriptContent: isGa ? null : draft.scriptContent,
      placement: draft.placement,
      status: draft.status,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{target ? '외부 서비스 수정' : '외부 서비스 추가'}</DialogTitle>
          <DialogDescription>
            GA는 측정 ID를, 그 외 분류는 스크립트 내용을 입력합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1">
          {error ? (
            <div
              className="rounded-md px-3 py-2 text-[13px]"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <FormSection label="분류" density="compact">
            <RadioGroup
              value={draft.category}
              onValueChange={(v) => set('category', v as ExternalScriptCategory)}
              options={categoryOptions}
              orientation="horizontal"
              disabled={saving}
              aria-label="분류"
            />
          </FormSection>

          <FormSection label="서비스명" density="compact" required>
            <Input
              value={draft.serviceName}
              onChange={(e) => set('serviceName', e.target.value)}
              placeholder="예: Google Analytics 4, Meta Pixel"
              maxLength={120}
              disabled={saving}
            />
          </FormSection>

          {isGa ? (
            <FormSection label="측정 ID" density="compact" required>
              <Input
                value={draft.measurementId}
                onChange={(e) => set('measurementId', e.target.value)}
                placeholder="G-XXXXXXXXXX"
                maxLength={64}
                disabled={saving}
                className="max-w-[280px]"
              />
            </FormSection>
          ) : (
            <FormSection label="스크립트" density="compact" required>
              <textarea
                value={draft.scriptContent}
                onChange={(e) => set('scriptContent', e.target.value)}
                placeholder="<script>…</script> 또는 임베드 코드"
                disabled={saving}
                rows={6}
                className="w-full rounded-md border px-3 py-2 font-mono text-[12px] outline-none transition-colors focus-visible:[box-shadow:var(--focus-ring-brand)] disabled:opacity-50"
                style={{
                  background: 'var(--surface-0)',
                  color: 'var(--content-strong)',
                  borderColor: 'var(--divider-strong)',
                }}
              />
            </FormSection>
          )}

          <FormSection label="삽입 위치" density="compact">
            <RadioGroup
              value={draft.placement}
              onValueChange={(v) => set('placement', v as ScriptPlacement)}
              options={placementOptions}
              orientation="horizontal"
              disabled={saving}
              aria-label="삽입 위치"
            />
          </FormSection>

          <FormSection label="상태" density="compact">
            <RadioGroup
              value={draft.status}
              onValueChange={(v) => set('status', v as ExternalScriptStatus)}
              options={statusOptions}
              orientation="horizontal"
              disabled={saving}
              aria-label="상태"
            />
          </FormSection>

          <FormSection label="정렬 순서" density="compact">
            <Input
              type="number"
              value={draft.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              className="max-w-[120px]"
              disabled={saving}
            />
          </FormSection>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            취소
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
